/**
 * How a Lottie icon handles a dark surface.
 *
 * These icons are drawn as black strokes with a blue accent and white interior fills, on
 * transparency, which assumes a light surface. Something has to give when the same file
 * sits on a dark card, and CSS filters cannot fix it: there is no way to lighten a black
 * stroke without also darkening the white fills, because a filter cannot tell them apart.
 *
 * Two options survive. A third, `invert` plus `hue-rotate(180deg)`, was built and
 * rejected: it does turn black strokes white while holding the blue, but it also drives
 * the white interiors near-black, so icons read as dark slabs, worst of all on the
 * lighter `gray-700` cards. Recorded here so nobody spends the afternoon rediscovering
 * it.
 */
export type DarkTreatment = 'silhouette' | 'none'

export const DARK_TREATMENTS: Record<DarkTreatment, string> = {
  /**
   * Flatten to a solid white shape. Drives every colour to black, then inverts.
   *
   * The site's original treatment, and the default so that nothing outside the sections
   * which opt out changes. Know what it costs: it works for an icon made only of
   * strokes and destroys any icon with filled interiors, which comes out as a
   * featureless white blob. The firewall, analytics and App Store icons all did.
   */
  silhouette: 'dark:invert dark:brightness-0 dark:contrast-200',

  /**
   * Leave the artwork exactly as drawn, in both themes.
   *
   * The black outlines recede on a dark card, but the blue accents and the white
   * interior fills carry the shape, and the icon keeps the colours it was designed
   * with. This is the right choice wherever the icon has enough filled area to read on
   * its own, which is all of the current set, and the only choice that makes dark mode
   * look like light mode rather than like a processed version of it.
   */
  none: '',
}
